// Procedural math question generator — produces 10 fresh questions per level

function rnd(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min; }
function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5); }

function wrongNums(correct, min, max, count = 3) {
  const s = new Set([correct]);
  while (s.size < count + 1) {
    let n = rnd(Math.max(0, correct - 10), correct + 10);
    if (n !== correct && n >= min && n <= max) s.add(n);
    else s.add(correct + s.size); // fallback
  }
  return [...s].filter(n => n !== correct).slice(0, count).map(String);
}

function mcNum(q, correct, min, max, e) {
  const opts = shuffle([String(correct), ...wrongNums(correct, min, max)]);
  return { l: 0, t: 'mc', q, o: opts, a: String(correct), e };
}

export function generateMathQuestions(level) {
  const qs = [];
  switch (level) {
    case 1: { // Counting 1–10
      for (let i = 0; i < 5; i++) {
        const n = rnd(1, 9);
        const after = n + 1;
        qs.push(mcNum(`What number comes AFTER ${n}? 🔢`, after, 1, 10, `After ${n} comes ${after} — keep counting!`));
      }
      for (let i = 0; i < 3; i++) {
        const n = rnd(2, 10);
        const before = n - 1;
        qs.push(mcNum(`What number comes BEFORE ${n}? 🔢`, before, 1, 10, `Before ${n} is ${before}!`));
      }
      const sides = [['triangle','🔺',3],['square','⬛',4],['circle','⭕',0],['pentagon','⬠',5]];
      const s = pick(sides.filter(x=>x[2]>0));
      qs.push(mcNum(`How many sides does a ${s[0]} ${s[1]} have?`, s[2], 1, 8, `A ${s[0]} has ${s[2]} sides!`));
      const evens = [2,4,6,8,10];
      const e = pick(evens);
      qs.push({l:1,t:'tf',q:`Is ${e} an even number?`,a:'True',e:`${e} is even! Even numbers: 2, 4, 6, 8, 10...`});
      break;
    }
    case 2: { // Counting to 20, skip counting by 2s and 5s
      for (let i = 0; i < 4; i++) {
        const start = rnd(1, 18);
        qs.push(mcNum(`Count by 1s: what comes after ${start}?`, start+1, 1, 20, `Just add 1! ${start} + 1 = ${start+1}.`));
      }
      for (let i = 0; i < 3; i++) {
        const base = rnd(1,8)*2;
        qs.push(mcNum(`Skip count by 2s: what comes after ${base}?`, base+2, 2, 20, `Counting by 2s: add 2 each time. ${base} + 2 = ${base+2}!`));
      }
      for (let i = 0; i < 3; i++) {
        const base = rnd(1,3)*5;
        qs.push(mcNum(`Skip count by 5s: what comes after ${base}?`, base+5, 5, 25, `Counting by 5s: ${base} + 5 = ${base+5}!`));
      }
      break;
    }
    case 3: { // Addition within 10
      for (let i = 0; i < 7; i++) {
        const a = rnd(1, 9), b = rnd(1, 10 - a);
        const ans = a + b;
        qs.push({ l:3, t:'fill', q:`${a} + ${b} = ?`, a: String(ans), e:`${a} + ${b} = ${ans}. Count up from ${a}: ${Array.from({length:b},(_,k)=>a+k+1).join(', ')}!` });
      }
      for (let i = 0; i < 3; i++) {
        const a = rnd(1,5), b = rnd(1,5), ans = a+b;
        qs.push(mcNum(`${a} + ${b} = ?`, ans, 1, 10, `${a} + ${b} = ${ans}!`));
      }
      break;
    }
    case 4: { // Addition within 20
      for (let i = 0; i < 5; i++) {
        const a = rnd(5,15), b = rnd(2,20-a);
        const ans = a+b;
        qs.push({ l:4, t:'fill', q:`${a} + ${b} = ?`, a: String(ans), e:`${a} + ${b} = ${ans}. Start at ${a} and count up ${b}!` });
      }
      for (let i = 0; i < 3; i++) {
        const a = rnd(3,12), b = rnd(3,12);
        const ans = a+b;
        qs.push(mcNum(`${a} + ${b} = ?`, ans, 6, 24, `${a} + ${b} = ${ans}!`));
      }
      qs.push({l:4,t:'tf',q:`7 + 8 = 16`,a:'False',e:`7 + 8 = 15, not 16. Count up 8 from 7: 8,9,10,11,12,13,14,15!`});
      qs.push({l:4,t:'tf',q:`9 + 9 = 18`,a:'True',e:`9 + 9 = 18. Doubles are easy to remember!`});
      break;
    }
    case 5: { // Subtraction within 20
      for (let i = 0; i < 5; i++) {
        const total = rnd(8,20), sub = rnd(2,total-1), ans = total-sub;
        qs.push({ l:5, t:'fill', q:`${total} − ${sub} = ?`, a: String(ans), e:`${total} − ${sub} = ${ans}. Count back ${sub} from ${total}!` });
      }
      for (let i = 0; i < 5; i++) {
        const total = rnd(6,20), sub = rnd(2,total-1), ans = total-sub;
        qs.push(mcNum(`${total} − ${sub} = ?`, ans, 0, 18, `${total} − ${sub} = ${ans}. Take away ${sub} from ${total}!`));
      }
      break;
    }
    case 6: { // Addition & subtraction within 100
      for (let i = 0; i < 4; i++) {
        const a = rnd(10,80), b = rnd(5,99-a), ans = a+b;
        qs.push({ l:6, t:'fill', q:`${a} + ${b} = ?`, a: String(ans), e:`${a} + ${b} = ${ans}. Add tens first, then ones!` });
      }
      for (let i = 0; i < 4; i++) {
        const total = rnd(20,99), sub = rnd(5,total-5), ans = total-sub;
        qs.push(mcNum(`${total} − ${sub} = ?`, ans, 0, 94, `${total} − ${sub} = ${ans}. Subtract tens first, then ones!`));
      }
      qs.push({l:6,t:'tf',q:`55 + 45 = 100`,a:'True',e:`55 + 45 = 100. The ones add to 10 and carry, giving you 100!`});
      qs.push({l:6,t:'tf',q:`80 − 35 = 55`,a:'False',e:`80 − 35 = 45, not 55. 80 − 30 = 50, then 50 − 5 = 45!`});
      break;
    }
    case 7: { // Multiplication tables 2–5
      const tables = [2,3,4,5];
      for (let i = 0; i < 7; i++) {
        const t = pick(tables), n = rnd(2,10), ans = t*n;
        qs.push({ l:7, t:'fill', q:`${t} × ${n} = ?`, a: String(ans), e:`${t} × ${n} = ${ans}. Count by ${t}s: ${Array.from({length:n},(_,k)=>(k+1)*t).join(', ')}!` });
      }
      for (let i = 0; i < 3; i++) {
        const t = pick(tables), n = rnd(2,10), ans = t*n;
        qs.push(mcNum(`${t} × ${n} = ?`, ans, 4, 50, `${t} × ${n} = ${ans}!`));
      }
      break;
    }
    case 8: { // Multiplication tables 6–12
      const tables = [6,7,8,9,11,12];
      for (let i = 0; i < 5; i++) {
        const t = pick(tables), n = rnd(2,12), ans = t*n;
        qs.push({ l:8, t:'fill', q:`${t} × ${n} = ?`, a: String(ans), e:`${t} × ${n} = ${ans}. Practice your ${t}s table!` });
      }
      for (let i = 0; i < 5; i++) {
        const t = pick(tables), n = rnd(2,12), ans = t*n;
        qs.push(mcNum(`${t} × ${n} = ?`, ans, 12, 144, `${t} × ${n} = ${ans}!`));
      }
      break;
    }
    case 9: { // Division
      const pairs = [[2,3],[2,4],[2,5],[2,6],[3,3],[3,4],[3,5],[4,3],[4,4],[5,3],[5,4],[6,3],[6,4],[7,3],[8,3],[9,3],[9,4],[6,7],[7,8],[8,9]];
      for (let i = 0; i < 7; i++) {
        const [div,n] = pick(pairs), total = div*n;
        qs.push({ l:9, t:'fill', q:`${total} ÷ ${div} = ?`, a: String(n), e:`${total} ÷ ${div} = ${n} because ${div} × ${n} = ${total}!` });
      }
      for (let i = 0; i < 3; i++) {
        const [div,n] = pick(pairs), total = div*n;
        qs.push(mcNum(`${total} ÷ ${div} = ?`, n, 2, 12, `${total} ÷ ${div} = ${n} because ${div} × ${n} = ${total}!`));
      }
      break;
    }
    case 10: { // Fractions & decimals
      const fracs = [
        {q:'What is 1/2 of 20?',a:10,e:'1/2 of 20: divide by 2. 20 ÷ 2 = 10!'},
        {q:'What is 1/4 of 16?',a:4,e:'1/4 of 16: divide by 4. 16 ÷ 4 = 4!'},
        {q:'What is 3/4 of 12?',a:9,e:'3/4 of 12: 1/4=3, then ×3 = 9!'},
        {q:'What is 1/3 of 9?',a:3,e:'1/3 of 9: divide by 3. 9 ÷ 3 = 3!'},
        {q:'What is 2/3 of 12?',a:8,e:'2/3 of 12: 1/3=4, then ×2 = 8!'},
      ];
      for (let i = 0; i < 5; i++) {
        const f = fracs[i];
        qs.push(mcNum(f.q, f.a, 1, 20, f.e));
      }
      const decs = [
        {q:'0.5 + 0.5 = ?',a:'1',e:'0.5 + 0.5 = 1.0 — two halves make a whole!'},
        {q:'0.25 + 0.75 = ?',a:'1',e:'0.25 + 0.75 = 1.00 — a quarter plus three quarters!'},
        {q:'1.5 + 1.5 = ?',a:'3',e:'1.5 + 1.5 = 3.0 — two one-and-a-halfs!'},
        {q:'2.5 − 1.0 = ?',a:'1.5',e:'2.5 − 1.0 = 1.5 — subtract the whole number!'},
        {q:'0.1 + 0.9 = ?',a:'1',e:'0.1 + 0.9 = 1.0 — they add up to one whole!'},
      ];
      for (let i = 0; i < 5; i++) {
        const d = decs[i];
        qs.push({l:10,t:'fill',q:d.q,a:d.a,e:d.e});
      }
      break;
    }
    default: break;
  }
  // Tag with level and slice to exactly 10
  return qs.map(q => ({...q, l: level})).slice(0, 10);
}
